// Helpers for previewing the slideshow fonts that the video micro-service
// exposes via GET /listing/slideshow/fonts. The service returns raw TTF file
// names (e.g. "SourceSans3-SemiBoldItalic.ttf"); these helpers turn a file name
// into the metadata needed to render the option in its real typeface, loaded
// from Google Fonts the same way the lead-gen PostGraphicsEditor does.

export interface SlideshowFontMeta {
    /** The original file name — this stays the value submitted to the backend. */
    file: string;
    /** Friendly label shown in the dropdown (extension stripped). */
    label: string;
    /** CSS `font-family` value to render the preview, with a system fallback. */
    cssFamily: string;
    /** Google Fonts family to load, or null when no web font is needed. */
    googleFamily: string | null;
    /** True when the face is bundled via a self-hosted @font-face (no Google
     * Fonts link, but the canvas preview must still wait for it to load). */
    selfHosted: boolean;
    /** Numeric font weight parsed from the file name (default 400). */
    weight: number;
    /** Font style parsed from the file name. */
    style: "normal" | "italic";
}

// Strip a font file extension for a friendlier label.
const stripExtension = (font: string): string =>
    font.replace(/\.(ttf|otf|woff2?)$/i, "");

// Family token (the part before the first "-") → web-font mapping. Only the
// families actually shipped in marabot-video2/fonts are listed; unknown tokens
// fall back to a best-effort CamelCase → spaced family below.
//
// IMPORTANT: keep this in sync with the font families in marabot-video2/fonts/.
// If the video service adds a new family, add it here too — otherwise the
// fallback guesses a Google Fonts family name that may not exist, the
// stylesheet request 404s silently, and the preview drops to plain sans-serif.
const FAMILY_MAP: Record<
    string,
    { googleFamily: string | null; cssFamily: string; selfHosted?: boolean }
> = {
    Inter: { googleFamily: "Inter", cssFamily: "'Inter', sans-serif" },
    // Inter Display is the display-optical cut of Inter — a genuinely distinct
    // face the video service renders from its own InterDisplay TTFs, NOT a
    // Google Fonts family. Self-host it (src/index.css @font-face) so the live
    // preview matches the rendered slideshow instead of silently substituting
    // plain Inter.
    InterDisplay: {
        googleFamily: null,
        cssFamily: "'Inter Display', sans-serif",
        selfHosted: true,
    },
    SourceSans3: {
        googleFamily: "Source Sans 3",
        cssFamily: "'Source Sans 3', sans-serif",
    },
    // Liberation Sans is metric-identical to Arial and not on Google Fonts.
    LiberationSans: {
        googleFamily: null,
        cssFamily: "Arial, Helvetica, sans-serif",
    },
};

// Weight word → numeric weight. "Regular" and absence both mean 400.
const WEIGHT_MAP: Record<string, number> = {
    Thin: 100,
    ExtraLight: 200,
    Light: 300,
    Regular: 400,
    Medium: 500,
    SemiBold: 600,
    Bold: 700,
    ExtraBold: 800,
    Black: 900,
};

// "SourceSans3" → "Source Sans 3"; leaves trailing digits attached to letters.
const camelToSpaced = (token: string): string =>
    token
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/([A-Za-z])(\d)/g, "$1 $2")
        .trim();

export const parseSlideshowFont = (file: string): SlideshowFontMeta => {
    const base = stripExtension(file);
    const dashIndex = base.indexOf("-");
    const token = dashIndex === -1 ? base : base.slice(0, dashIndex);
    const suffix = dashIndex === -1 ? "" : base.slice(dashIndex + 1);

    const style: "normal" | "italic" = /italic/i.test(suffix) ? "italic" : "normal";

    // Drop the "Italic" marker, then match the remaining weight word.
    const weightWord = suffix.replace(/italic/i, "");
    const weight =
        Object.entries(WEIGHT_MAP).find(
            ([word]) => word.toLowerCase() === weightWord.toLowerCase(),
        )?.[1] ?? 400;

    const mapped = FAMILY_MAP[token];
    const family = mapped ?? {
        googleFamily: camelToSpaced(token),
        cssFamily: `'${camelToSpaced(token)}', sans-serif`,
    };

    return {
        file,
        label: camelToSpaced(base),
        cssFamily: family.cssFamily,
        googleFamily: family.googleFamily,
        selfHosted: family.selfHosted ?? false,
        weight,
        style,
    };
};

// Build the Google Fonts CSS2 stylesheet URL for a family. We only need the
// Regular (400) weight — that's what the dropdown previews render and what the
// canvas preview draws. Requesting a wide axis (e.g. 100..900) makes Google
// Fonts reject the whole stylesheet for families whose true axis is narrower
// (Source Sans 3 starts at 200), so the font silently fails to load; weight 400
// is supported by every family, so it always resolves.
export const googleFontsHref = (family: string): string => {
    // Encode the family name, then restore Google's "+" convention for spaces.
    const encoded = encodeURIComponent(family).replace(/%20/g, "+");
    return `https://fonts.googleapis.com/css2?family=${encoded}:wght@400&display=swap`;
};
